import {
  Box,
  Button,
  Modal,
  Paper,
  TextField,
  Typography,
  Table,
  TableCell,
  TableBody,
  TableContainer,
  styled,
  TableHead,
  TablePagination,
  TableRow,
  tableCellClasses,
} from "@mui/material";

import React, { forwardRef, useContext, useEffect, useState } from "react";
import { useSpring, animated } from "@react-spring/web";
import PropTypes from "prop-types";
import Backdrop from "@mui/material/Backdrop";
import { cloneElement } from "react";
import DeclareContext from "../context/users/DeclareContext";

const Fade = forwardRef(function Fade(props, ref) {
  const {
    children,
    in: open,
    onClick,
    onEnter,
    onExited,
    ownerState,
    ...other
  } = props;

  const style = useSpring({
    from: { opacity: 0 },
    to: { opacity: open ? 1 : 0 },
    onStart: () => {
      if (open && onEnter) {
        onEnter(null, true);
      }
    },
    onRest: () => {
      if (!open && onExited) {
        onExited(null, true);
      }
    },
  });

  return (
    <animated.div ref={ref} style={style} {...other}>
      {cloneElement(children, { onClick })}
    </animated.div>
  );
});

Fade.propTypes = {
  children: PropTypes.element.isRequired,
  in: PropTypes.bool,
  onClick: PropTypes.any,
  onEnter: PropTypes.func,
  onExited: PropTypes.func,
  ownerState: PropTypes.any,
};

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  minWidth: 300,
  p: 4,
  "@media (min-width:600px)": {
    width: "80%", // 화면이 넓어지면 폭을 줄임
  },
  "@media (min-width:960px)": {
    width: "90%", // 데스크톱 화면에서 폭을 더 줄임
    maxWidth: 800, // 더 넓은 화면에서 최대 폭을 설정
  },
  "@media (min-width:1280px)": {
    width: "50%", // 큰 화면에서는 더 줄임
    maxWidth: 800, // 최대 폭을 더 넓게 설정
  },
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#373A40",
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

export default function AddrSearchModal(props) {
  const { searchType } = props;
  const {
    roadSearch,
    setRoadSearch,
    roadSearchList,
    setRoadSearchList,
    getJibunSearchList,
    jibunSearch,
    setJibunSearch,
    jibunSearchList,
    setJibunSearchList,
    getRoadSearchList,

    setTargetDataList,
    handleSelectLocation,
  } = useContext(DeclareContext);
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);

  const handleClose = () => {
    setJibunSearchList("");
    setRoadSearchList("");
    setRoadSearch("");
    setJibunSearch("");
    setOpen(false);
  };

  const handleSearch = async () => {
    try {
      let results;
      if (searchType === 0) {
        // 도로명 주소 검색
        results = await getRoadSearchList();
      } else if (searchType === 1) {
        // 지번 주소 검색
        results = await getJibunSearchList();
      }

      // Assuming results have a 'data' property containing the address list
      if (results && results.data) {
        // Store the addresses in the target data list
        setTargetDataList(
          results.data.map((item) => ({
            address: item.address,
            xpos: item.xpos,
            ypos: item.ypos,
          }))
        );
      }
    } catch (error) {
      console.error("검색 중 오류 발생:", error);
    }
  };

  const [error, setError] = useState("");
  const handleInputChange = (e) => {
    const inputValue = e.target.value;

    // 영문 문자가 포함되어 있는지 검사
    if (/[a-zA-Z]/.test(inputValue)) {
      setError("한글과 숫자, 하이픈(-)만 입력할 수 있습니다.");
    } else {
      setError(""); // 영문이 없으면 에러 메시지 초기화
    }

    // 한글, 숫자, 공백, 하이픈만 허용
    const filteredValue = inputValue.replace(/[^ㄱ-ㅎ가-힣0-9\s-]/g, "");

    if (searchType === 0) {
      setRoadSearch(filteredValue);
    } else if (searchType === 1) {
      setJibunSearch(filteredValue);
    }
  };

  const handleEnter = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // pagenagion
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  useEffect(() => {
    if (open) {
      setTargetDataList([]);
    }
  }, [open, setRoadSearchList, setJibunSearchList]);

  return (
    <div>
      <Button variant="contained" onClick={handleOpen}>
        주소 찾기
      </Button>
      <Modal
        aria-labelledby="spring-modal-title"
        aria-describedby="spring-modal-description"
        open={open}
        onClose={handleClose}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            TransitionComponent: Fade,
          },
        }}
      >
        <Fade in={open}>
          <Box sx={style}>
            <Typography id="spring-modal-title" variant="h6" component="h2">
              {searchType === 0 ? "도로명" : "지번"} 주소 검색
            </Typography>
            <Typography id="spring-modal-description" sx={{ mt: 2 }}>
              검색어는 3글자 이상 입력 바랍니다.
            </Typography>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
              }}
            >
              <TextField
                type="text"
                autoComplete="off"
                autoFocus
                value={searchType === 0 ? roadSearch : jibunSearch}
                onKeyDown={handleEnter}
                onChange={handleInputChange}
                error={!!error}
                label={error || "주소를 입력하세요"}
                sx={{ mt: 2, mb: 2, width: "80%" }}
              />

              <Button
                className="list-button"
                variant="contained"
                onClick={handleSearch}
                sx={{ mb: 2 }}
              >
                검색
              </Button>
            </div>
            {searchType === 0 && (
              <>
                {roadSearchList.length > 0 ? ( // 도로명 검색 결과 렌더링
                  <Paper sx={{ overflow: "hidden" }}>
                    <TableContainer component={Paper} sx={{ maxHeight: 440 }}>
                      <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                          <TableRow>
                            <StyledTableCell align="center">
                              도로명 주소
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              선택
                            </StyledTableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {roadSearchList
                            .slice(
                              page * rowsPerPage,
                              page * rowsPerPage + rowsPerPage
                            )
                            .map((item, index) => (
                              <TableRow key={index}>
                                <StyledTableCell align="center">
                                  {item.address}
                                </StyledTableCell>
                                <StyledTableCell align="center">
                                  <Button
                                    variant="outlined"
                                    onClick={() => {
                                      handleSelectLocation(
                                        item.xpos,
                                        item.ypos,
                                        item.address
                                      );
                                      handleClose();
                                    }}
                                  >
                                    선택
                                  </Button>
                                </StyledTableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      rowsPerPageOptions={[10, 25, 50]} // 페이지당 항목 수 옵션
                      component="div"
                      count={roadSearchList.length} // 총 항목 수
                      rowsPerPage={rowsPerPage} // 현재 페이지당 항목 수
                      page={page} // 현재 페이지
                      onPageChange={(event, newPage) => setPage(newPage)} // 페이지 변경 핸들러
                      onRowsPerPageChange={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10));
                        setPage(0); // 페이지 수 변경 시 첫 페이지로 이동
                      }}
                    />
                  </Paper>
                ) : (
                  <Typography align="center" sx={{ padding: 2 }}>
                    조회되는 주소 리스트가 없습니다.
                  </Typography>
                )}
              </>
            )}

            {searchType === 1 && (
              <>
                {jibunSearchList.length > 0 ? ( // 지번 검색 결과 렌더링
                  <Paper sx={{ overflow: "hidden" }}>
                    <TableContainer component={Paper} sx={{ maxHeight: 440 }}>
                      <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                          <TableRow>
                            <StyledTableCell align="center">
                              지번 주소
                            </StyledTableCell>
                            <StyledTableCell align="center">
                              선택
                            </StyledTableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {jibunSearchList
                            .slice(
                              page * rowsPerPage,
                              page * rowsPerPage + rowsPerPage
                            )
                            .map((item, index) => (
                              <TableRow key={index}>
                                <StyledTableCell align="center">
                                  {item.address}
                                </StyledTableCell>
                                <StyledTableCell align="center">
                                  <Button
                                    variant="outlined"
                                    onClick={() => {
                                      handleSelectLocation(
                                        item.xpos,
                                        item.ypos,
                                        item.address
                                      );
                                      handleClose();
                                    }}
                                  >
                                    선택
                                  </Button>
                                </StyledTableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table> 
                    </TableContainer>
                    <TablePagination
                      rowsPerPageOptions={[10, 20, 50]} // 페이지당 항목 수 옵션
                      component="div"
                      count={jibunSearchList.length} // 총 항목 수
                      rowsPerPage={rowsPerPage} // 현재 페이지당 항목 수
                      page={page} // 현재 페이지
                      onPageChange={(event, newPage) => setPage(newPage)} // 페이지 변경 핸들러
                      onRowsPerPageChange={(event) => {
                        setRowsPerPage(parseInt(event.target.value, 10));
                        setPage(0); // 페이지 수 변경 시 첫 페이지로 이동
                      }}
                    />
                  </Paper>
                ) : (
                  <Typography align="center" sx={{ padding: 2 }}>
                    조회되는 주소 리스트가 없습니다.
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Fade>
      </Modal>
    </div>
  );
}
